import { combineReducers } from 'redux';
import { get } from 'lodash';
import * as AccountPagingUtils from './utils/paging';
import { formatAccounts } from './utils/account-parsing/formatAccounts';

import {
	SET_ACCOUNTS_SEARCH_TEXT,
	GET_ACCOUNTS_PAGE_SUCCESS,
	GET_ACCOUNTS_PAGE_FAILURE,
	GET_ACCOUNTS_PAGE_REQUEST,
	SAVE_LAST_API_ACTION,
	DEFAULT_PAGE_SIZE,
	GET_PAYEES_PAGE_SUCCESS,
	GET_PAYEES_PAGE_REQUEST,
	GET_PAYEES_PAGE_FAILURE,
	DEFAULT_PAYEES_PAGE_SIZE,
	SET_SHOW_PAYEES,
	SET_PAGING_LAYOUT_TYPE,
	SET_PAYEES_SEARCH_TEXT,
	SET_PAYEES_PAGE_SIZE,
	SET_ACCOUNTS_PAGE_SIZE,
	SET_PAYEES_CURRENT_PAGE,
	SET_ACCOUNTS_CURRENT_PAGE,
	RESET_PAGED_PAYEES,
	SET_IS_SELECTING_LCV_FROM_ACCOUNT,
} from './constants';
import {
	GET_FAVOURITE_ACCOUNTS_SUCCESS,
	UPDATE_REMOVE_FAVOURITE_ACCOUNT,
	UPDATE_ADD_FAVOURITE_ACCOUNT,
} from '../favourites/actions';
import { RESET_APP } from '../page/actions';

const accounts = (state = [], action) => {
	switch (action.type) {
		case GET_ACCOUNTS_PAGE_SUCCESS:
			return formatAccounts(action.payload.accounts);
		case RESET_APP:
			return [];
		default:
			return state;
	}
};

const accountsNeverFetched = (state = true, action) => {
	switch (action.type) {
		case GET_ACCOUNTS_PAGE_SUCCESS:
			return false;
		case RESET_APP:
			return true;
		default:
			return state;
	}
};

const lastFailedApiAction = (state = null, action) => {
	switch (action.type) {
		case SAVE_LAST_API_ACTION:
			return AccountPagingUtils.copyLastFailedApiAction(action.lastFailedApiAction);
		case GET_ACCOUNTS_PAGE_SUCCESS:
		case GET_PAYEES_PAGE_SUCCESS:
			return state;
		default:
			return state;
	}
};

const loading = (state = false, action) => {
	switch (action.type) {
		case GET_ACCOUNTS_PAGE_REQUEST:
		case GET_PAYEES_PAGE_REQUEST:
			return true;
		case GET_ACCOUNTS_PAGE_SUCCESS:
		case GET_ACCOUNTS_PAGE_FAILURE:
		case GET_PAYEES_PAGE_SUCCESS:
		case GET_PAYEES_PAGE_FAILURE:
			return false;
		default:
			return state;
	}
};

const error = (state = false, action) => {
	switch (action.type) {
		case GET_ACCOUNTS_PAGE_REQUEST:
		case GET_PAYEES_PAGE_REQUEST:
			return false;
		case GET_ACCOUNTS_PAGE_SUCCESS:
		case GET_PAYEES_PAGE_SUCCESS:
			return false;
		case GET_ACCOUNTS_PAGE_FAILURE:
		case GET_PAYEES_PAGE_FAILURE:
			return true;
		default:
			return state;
	}
};

const favourites = (state = { accounts: [] }, action) => {
	switch (action.type) {
		case GET_FAVOURITE_ACCOUNTS_SUCCESS:
			return {
				...action.payload,
				accounts: formatAccounts(get(action, 'payload.accounts')),
				total_number_of_accounts: get(action, 'payload.total_number_of_accounts', 0),
			};
		case UPDATE_REMOVE_FAVOURITE_ACCOUNT:
			return { ...state, accounts: state.accounts.filter((account) => account.id !== action.id) };
		case UPDATE_ADD_FAVOURITE_ACCOUNT:
			return { ...state, accounts: [...state.accounts, action.account] };
		default:
			return state;
	}
};
const payees = (state = [], action) => {
	switch (action.type) {
		case GET_PAYEES_PAGE_SUCCESS:
			return get(action, 'payload.beneficiaries');
		case RESET_PAGED_PAYEES:
			return [];
		default:
			return state;
	}
};

const payeesMetaDataInitialState = {
	pageSize: DEFAULT_PAYEES_PAGE_SIZE,
	totalNumberOfPages: 1,
	currentPage: 1,
	showPayees: true,
	searchText: '',
	payeesNeverFetched: true,
};

const payeesMetaData = (state = payeesMetaDataInitialState, action) => {
	switch (action.type) {
		case GET_PAYEES_PAGE_SUCCESS:
			return {
				...state,
				pageSize: state.pageSize,
				totalNumberOfPages: get(action, 'payload.page_meta_data.total_pages'),
				currentPage: get(action, 'payload.page_meta_data.page_number'),
				payeesNeverFetched: false,
				isSearchFilterOn: !!get(action, 'payload.page_meta_data.search') || !!get(action, 'meta.searchText'),
			};
		case SET_PAYEES_SEARCH_TEXT:
			return { ...state, searchText: action.searchText };
		case SET_SHOW_PAYEES:
			return { ...state, showPayees: action.showPayees };
		case SET_PAYEES_PAGE_SIZE:
			return { ...state, pageSize: action.pageSize };
		case SET_PAYEES_CURRENT_PAGE:
			return { ...state, currentPage: action.page };
		case RESET_PAGED_PAYEES:
			return { ...payeesMetaDataInitialState, showPayees: state.showPayees };
		default:
			return state;
	}
};

const accountsMetaData = (
	state = {
		pageSize: DEFAULT_PAGE_SIZE,
		totalNumberOfPages: 1,
		currentPage: 1,
		searchText: '',
	},
	action
) => {
	switch (action.type) {
		case GET_ACCOUNTS_PAGE_SUCCESS:
			return {
				...state,
				pageSize: state.pageSize,
				totalNumberOfPages: get(action, 'payload.page_meta_data.total_pages'),
				currentPage: get(action, 'payload.page_meta_data.page_number'),
				isSearchFilterOn: !!get(action, 'payload.page_meta_data.search_text'),
			};
		case SET_ACCOUNTS_SEARCH_TEXT:
			return { ...state, searchText: action.searchText };
		case SET_ACCOUNTS_PAGE_SIZE:
			return { ...state, pageSize: action.pageSize };
		case SET_ACCOUNTS_CURRENT_PAGE:
			return { ...state, currentPage: action.page };
		default:
			return state;
	}
};

const layoutType = (state = '', action) => {
	switch (action.type) {
		case SET_PAGING_LAYOUT_TYPE:
			return action.layoutType;
		default:
			return state;
	}
};

const isSelectingLCVFromAccount = (state = false, action) => {
	switch (action.type) {
		case SET_IS_SELECTING_LCV_FROM_ACCOUNT:
			return action.payload;
		case RESET_APP:
			return false;
		default:
			return state;
	}
};

export default combineReducers({
	loading,
	error,
	lastFailedApiAction,
	accounts,
	accountsNeverFetched,
	favourites,
	payees,
	payeesMetaData,
	accountsMetaData,
	layoutType,
	isSelectingLCVFromAccount,
});
